/**
 * Off-main-thread JSON parser for large FactSet intent payloads.
 *
 * Some intent responses arrive doubly-encoded — the host wraps the actual
 * payload as an escaped JSON string inside the wire envelope. On large decks
 * (200+ slides), the inner `result` string can exceed 15 MB and the
 * subsequent `JSON.parse` blocks the addin's single main thread for several
 * seconds, producing the user-visible freeze captured in the perf logs.
 *
 * `parseJsonInWorker` moves that parse into a singleton Web Worker so the
 * UI thread stays responsive. Three guardrails keep the helper safe to use
 * unconditionally:
 *
 *   1. **Inline fast path** for strings under `WORKER_PARSE_MIN_BYTES`.
 *      Below this size the worker round-trip costs more than the parse
 *      itself, so we just `JSON.parse` inline.
 *   2. **Sync fallback** when `typeof Worker === "undefined"` (vitest's
 *      jsdom env, SSR, or any host that strips Worker globals).
 *   3. **Failure fallback** — any worker error or hard-timeout (10 s) makes
 *      the wrapper fall back to a sync `JSON.parse` and emit a one-shot
 *      warning. The caller still gets a parsed object (or a throw from
 *      `JSON.parse` on malformed input); functional behaviour is unchanged.
 *
 * The worker itself is constructed from an inline `Blob` URL so we don't
 * need a separate webpack entry / `worker-loader` plumbing.
 */

import { createLogger } from "../../utils/logger";

const log = createLogger("JsonParseWorker");

/**
 * Below this byte-length we parse inline on the main thread. The handoff
 * cost of `postMessage` (structured-clone of the input string + the parsed
 * object back) only pays off once the parse itself is in the multi-hundred
 * millisecond range, which empirically starts around 256 KB on V8.
 */
const WORKER_PARSE_MIN_BYTES = 262_144;

/**
 * Hard wall-clock limit on a single worker parse. Exists so a hung worker
 * (extremely unlikely — `JSON.parse` is synchronous and CPU-bound) cannot
 * leave the caller awaiting forever. A timeout falls back to sync parse.
 */
const WORKER_PARSE_HARD_TIMEOUT_MS = 10_000;

const WORKER_SOURCE = `
self.onmessage = function (event) {
  var data = event.data || {};
  var id = data.id;
  var json = data.json;
  if (typeof json !== "string") {
    self.postMessage({ id: id, error: "json must be a string" });
    return;
  }
  try {
    var parsed = JSON.parse(json);
    self.postMessage({ id: id, result: parsed });
  } catch (err) {
    self.postMessage({
      id: id,
      error: err && err.message ? String(err.message) : "JSON.parse failed",
    });
  }
};
`;

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
  inputBytes: number;
  startedAt: number;
}

interface WorkerMessage {
  id: number;
  result?: unknown;
  error?: string;
}

let workerInstance: Worker | null = null;
let workerUrl: string | null = null;
let workerUnsupported = false;
let nextRequestId = 1;
const pending = new Map<number, PendingRequest>();

function isWorkerSupported(): boolean {
  if (workerUnsupported) return false;
  return (
    typeof Worker !== "undefined" &&
    typeof Blob !== "undefined" &&
    typeof URL !== "undefined" &&
    typeof URL.createObjectURL === "function"
  );
}

function failAllPending(reason: Error): void {
  // Map.forEach avoids the TS2802 downlevel-iteration requirement that
  // `for (const entry of pending.values())` triggers under the project's
  // current tsconfig target.
  pending.forEach((entry) => {
    clearTimeout(entry.timeoutId);
    entry.reject(reason);
  });
  pending.clear();
}

function teardownWorker(): void {
  if (workerInstance) {
    try {
      workerInstance.terminate();
    } catch {
      // ignore — termination is best-effort.
    }
    workerInstance = null;
  }
  if (workerUrl) {
    try {
      URL.revokeObjectURL(workerUrl);
    } catch {
      // ignore.
    }
    workerUrl = null;
  }
}

function handleWorkerMessage(event: MessageEvent<WorkerMessage>): void {
  const data = event.data;
  if (!data || typeof data.id !== "number") return;
  const entry = pending.get(data.id);
  if (!entry) return;
  pending.delete(data.id);
  clearTimeout(entry.timeoutId);
  if (typeof data.error === "string") {
    entry.reject(new Error(data.error));
    return;
  }
  log.debug(
    `parsed ${entry.inputBytes.toLocaleString()} bytes off main thread in ${
      Date.now() - entry.startedAt
    }ms`
  );
  entry.resolve(data.result);
}

function handleWorkerError(event: ErrorEvent | Event): void {
  const message =
    "message" in event && typeof (event as ErrorEvent).message === "string"
      ? (event as ErrorEvent).message
      : "worker error";
  log.warn(`worker errored, tearing down and falling back to sync: ${message}`);
  const error = new Error(message);
  failAllPending(error);
  workerUnsupported = true; // never retry; we'll use sync from here on.
  teardownWorker();
}

function ensureWorker(): Worker | null {
  if (workerInstance) return workerInstance;
  if (!isWorkerSupported()) return null;
  try {
    const blob = new Blob([WORKER_SOURCE], { type: "application/javascript" });
    workerUrl = URL.createObjectURL(blob);
    const worker = new Worker(workerUrl);
    worker.onmessage = handleWorkerMessage;
    worker.onerror = handleWorkerError;
    worker.onmessageerror = handleWorkerError;
    workerInstance = worker;
    log.debug("singleton worker created");
    return worker;
  } catch (err) {
    log.warn("failed to instantiate worker; falling back to sync parse", err);
    workerUnsupported = true;
    teardownWorker();
    return null;
  }
}

function syncParse<T>(json: string): T {
  return JSON.parse(json) as T;
}

/**
 * Parse a JSON string, preferring an off-main-thread worker for large
 * inputs. Always resolves to the parsed value (`T`) or rejects with the
 * underlying `JSON.parse` error.
 *
 * Behaviour:
 *   - For inputs below `WORKER_PARSE_MIN_BYTES`, parses inline.
 *   - For larger inputs in a Worker-capable environment, dispatches to the
 *     singleton worker via `postMessage`.
 *   - For larger inputs without Worker support (jsdom, SSR), or if the
 *     worker fails / times out, falls back to inline `JSON.parse`.
 */
export function parseJsonInWorker<T = unknown>(json: string): Promise<T> {
  if (typeof json !== "string") {
    return Promise.reject(new TypeError("parseJsonInWorker requires a string input"));
  }
  if (json.length < WORKER_PARSE_MIN_BYTES) {
    try {
      return Promise.resolve(syncParse<T>(json));
    } catch (err) {
      return Promise.reject(err instanceof Error ? err : new Error(String(err)));
    }
  }

  const worker = ensureWorker();
  if (!worker) {
    try {
      return Promise.resolve(syncParse<T>(json));
    } catch (err) {
      return Promise.reject(err instanceof Error ? err : new Error(String(err)));
    }
  }

  const id = nextRequestId++;
  const startedAt = Date.now();
  return new Promise<T>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      if (!pending.has(id)) return;
      pending.delete(id);
      log.warn(
        `worker parse timed out after ${WORKER_PARSE_HARD_TIMEOUT_MS}ms for ${json.length.toLocaleString()} bytes; falling back to sync`
      );
      try {
        resolve(syncParse<T>(json));
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    }, WORKER_PARSE_HARD_TIMEOUT_MS);

    pending.set(id, {
      resolve: resolve as (value: unknown) => void,
      reject,
      timeoutId,
      inputBytes: json.length,
      startedAt,
    });

    try {
      worker.postMessage({ id, json });
    } catch (err) {
      pending.delete(id);
      clearTimeout(timeoutId);
      log.warn("worker postMessage failed; falling back to sync parse", err);
      try {
        resolve(syncParse<T>(json));
      } catch (parseErr) {
        reject(parseErr instanceof Error ? parseErr : new Error(String(parseErr)));
      }
    }
  });
}

/**
 * Tear down the singleton worker and reject any pending parses. Intended
 * for tests that want a clean slate between cases; production code never
 * needs to call this.
 */
export function disposeJsonParseWorker(): void {
  failAllPending(new Error("disposeJsonParseWorker called"));
  teardownWorker();
  workerUnsupported = false;
  nextRequestId = 1;
}

/**
 * Threshold exported for tests so they can assert the inline-vs-worker
 * boundary without re-deriving the constant.
 */
export const __WORKER_PARSE_MIN_BYTES_FOR_TESTS = WORKER_PARSE_MIN_BYTES;
